import { Avatar, Box, Button, CircularProgress, Dialog, Modal, Snackbar } from "@mui/material"
import GIcon from "../GIcon"
import { ITopic, TopicTypes } from "../../types"
import { useCallback, useContext, useRef, useState } from "react"
import { useAtomInstance, useAtomState, useAtomValue } from '@zedux/react';
import { topicsAtom } from '../../atoms/topicsAtom'
import { dateToLast } from "../../utils"
import { Context } from "../ChatPanel/ChatContext"
import Confirmation from "../Confirmation"
import { messageReceiverAtom } from "../../atoms/messageReceiverAtom"
import { usersMapAtom } from "../../atoms/userListAtom"
import { userInfoAtom } from "../../atoms/socket"
import { onlineUsersAtom } from "../../atoms/onlineUsersAtom"
import { UserListPanel } from "../sideBar/UserListPanel"
import agoCalculator from "../../functions/agoCalculator";
import { convert_to_fa } from "@src/functions/farsi";
import ChangeProfilePic from "./ChangeProfilePic";
import useModalState from "@src/library/useModalState";

export interface ISettingModal {
    open: boolean,
    onClose: () => void,
    topic: ITopic
}

export function SettingsModal({ open, onClose, topic }: ISettingModal) {
    const [filteredName, setFilteredName] = useState("")
    const [loading, setLoading] = useState(false)
    const [pickGroup, openPickGroup, closePickGroup] = useModalState(false)
    const [toggle, setToggle] = useState(false)
    const userSet = useRef<Set<number>>(new Set())
    const [error, setError] = useState("")
    const [body, setBody] = useState("")
    const [onSubmit, setOnSubmit] = useState<(...args: any) => void>(() => { })
    const [rmUserOpen, openRmUser, closeRmUser] = useModalState(false)
    const [confirmLoading, setConfirmLoading] = useState(false)

    const { context } = useContext(Context)
    const { removeTopic, updateTopic } = useAtomInstance(topicsAtom, [context]).exports
    const { remove_group, group_add_user, group_remove_user, update_group } = useAtomInstance(messageReceiverAtom, [context]).exports
    const userInfo = useAtomValue(userInfoAtom)
    const onlineUsers = useAtomValue(onlineUsersAtom)
    const usersMap = useAtomValue(usersMapAtom)

    const label = topic.topic_type === TopicTypes.CHANNEL ? "کانال" : "گروه"

    // Resolve participant user IDs into IUserBasic from the usersMap
    const memberIds: number[] = Array.isArray(topic.participant_user_ids) ? topic.participant_user_ids : []
    const members = memberIds.map(id => usersMap[id]).filter(Boolean)

    const setImage = useCallback(async ({ image_id }) => {
        await update_group(topic.topic_id, { image_id })
    }, [topic.topic_id, update_group])

    const handleClose = () => {
        setError("")
    }

    const handleToggle = (userId: number) => {
        if (userSet.current.has(userId)) userSet.current.delete(userId)
        else userSet.current.add(userId)
        setToggle(v => !v)
    }

    // Add users to the group
    const handleAddUsers = async () => {
        const ids = Array.from(userSet.current)
        if (loading || ids.length === 0) return
        setLoading(true)
        try {
            const res = await group_add_user(topic.topic_id, ids)
            if (res?.success && res?.topic) {
                updateTopic(topic.topic_id, res.topic)
                userSet.current.clear()
                setToggle(v => !v)
                closePickGroup()
            } else {
                setError(res?.message ?? "خطا در افزودن عضو")
            }
        } catch (e: any) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }

    // Delete the group/channel
    function handleRemoveTopic() {
        setBody(`آیا مطمئن هستید که می‌خواهید ${label} "${topic.topic_name}" را حذف کنید؟`)
        openRmUser()
        setOnSubmit(() => async () => {
            setConfirmLoading(true)
            try {
                const res = await remove_group(topic.topic_id)
                if (res?.success) {
                    removeTopic(topic.topic_id)
                    onClose()
                } else {
                    setError(res?.message ?? "خطا در حذف")
                }
            } catch (e: any) {
                setError(e.message)
            } finally {
                setConfirmLoading(false)
                closeRmUser()
            }
        })
    }

    // Remove a single user from the group
    async function handleRemoveUser(user_id: number, fullname: string) {
        openRmUser()
        setBody(`آیا می‌خواهید "${fullname}" را از این ${label} حذف کنید؟`)
        setOnSubmit(() => async () => {
            setConfirmLoading(true)
            try {
                const res = await group_remove_user(topic.topic_id, user_id)
                if (!res?.success) {
                    setError(res?.message ?? "خطا در حذف عضو")
                }
            } catch (e: any) {
                setError(e.message)
            } finally {
                setConfirmLoading(false)
                closeRmUser()
            }
        })
    }

    return (
        <>
            <Modal
                className="flex justify-center items-center w-full h-full text-sm lg:text-base"
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                disableScrollLock
                open={open} onClose={onClose}>
                <Box className="flex flex-col bg-white rounded-lg" >
                    {/* Header */}
                    <div className="flex flex-row-reverse bg-stone-200 px-2 py-2 lg:py-8 rounded-t-lg w-full lg:w-160">
                        <GIcon className="mr-auto cursor-pointer" onClick={onClose} name="close" />
                        <div className="flex flex-row items-center">
                            <div className="mx-2 text-base lg:text-xl">{topic.topic_name}</div>
                        </div>
                    </div>

                    {/* Settings actions */}
                    <div className="flex-col bg-white border-b border-b-stone-300 flexcw:">
                        <span className="px-2 py-2 lg:py-4 text-stone-400 text-base lg:text-xl">تنظیمات</span>
                        <div
                            onClick={openPickGroup}
                            className="flex flex-row justify-start content-start hover:bg-stone-400/40 mx-4 py-2 cursor-pointer"
                        >
                            <span className="px-2 text-stone-800 text-base lg:text-xl">
                                <GIcon className="mx-2" name="person_add" />
                                <span>افزودن عضو جدید به {label}</span>
                            </span>
                        </div>
                        <div
                            onClick={handleRemoveTopic}
                            className="flex flex-row justify-start content-start hover:bg-stone-400/40 mx-4 py-2 cursor-pointer"
                        >
                            <span className="px-2 text-red-600 text-base lg:text-xl">
                                <GIcon className="mx-2" name="delete" />
                                حذف {label}
                            </span>
                        </div>
                        <div>
                            <ChangeProfilePic topic={topic} setImage={setImage} />
                        </div>
                        <div className="py-2" />
                    </div>

                    {/* Members list */}
                    <div className="py-2">
                        <span className="px-2 py-4 text-stone-400 text-base lg:text-xl">
                            اعضا ({convert_to_fa(memberIds.length)} نفر)
                        </span>
                        <div className="px-2 h-64 overflow-y-auto">
                            {members.length === 0 && (
                                <div className="flex justify-center items-center h-full text-stone-400">
                                    {memberIds.length > 0
                                        ? <CircularProgress size={24} />
                                        : "عضوی یافت نشد"}
                                </div>
                            )}
                            {members.map((u) => {
                                const isOnline = onlineUsers.has(u.id)
                                const isMe = String(u.id) === String(userInfo?.userId)
                                const isAdmin = topic.admin_id != null && u.id === topic.admin_id
                                return (
                                    <div key={u.id} className="flex flex-row items-center hover:bg-stone-400/40 py-2 rounded">
                                        <Avatar
                                            src={u.avatar_url}
                                            alt={u.username}
                                            className="mx-1 w-12 h-12"
                                        >
                                            {u.fullname?.[0]}
                                        </Avatar>
                                        <div className="flex-1 px-2 text-sm lg:text-xl">
                                            <div className="inline-block">{u.fullname}</div>
                                            {isAdmin && (
                                                <span className="px-2 text-stone-400 text-sm">مدیر</span>
                                            )}
                                        </div>
                                        <div className={`text-sm mx-2 ${isOnline ? "text-sky-500" : "text-stone-400"}`}>
                                            {isOnline ? "آنلاین" : (u.last_seen ? agoCalculator(u.last_seen as any) : "")}
                                        </div>
                                        <div className="ml-2">
                                            {isMe || isAdmin ? (
                                                <GIcon className="text-stone-300" name="star" />
                                            ) : (
                                                <GIcon
                                                    onClick={() => handleRemoveUser(u.id, u.fullname)}
                                                    className="text-stone-500 hover:text-red-500 cursor-pointer"
                                                    name="close"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </Box>
            </Modal>

            <Confirmation
                open={rmUserOpen}
                onClose={closeRmUser}
                onSubmit={onSubmit}
                title="آیا مطمئنید؟"
                body={body}
                loading={confirmLoading}
            />

            <Dialog
                open={pickGroup}
                onClose={() => { closePickGroup(); userSet.current.clear(); setFilteredName("") }}
                sx={{ "& .MuiDialog-container .MuiPaper-root": { width: "100%", maxWidth: "24rem", borderRadius: "1rem" } }}
                disableScrollLock
            >
                <div className="flex flex-col gap-3 p-4">
                    <p className="font-bold text-lg">افزودن عضو به {label}</p>
                    <input
                        value={filteredName}
                        onChange={e => setFilteredName(e.target.value)}
                        type="text"
                        className="p-2 border border-stone-200 rounded w-full"
                        placeholder="فیلتر سریع..."
                    />
                    <div className="max-h-80 overflow-y-auto">
                        <UserListPanel
                            mode="checkbox"
                            filteredName={filteredName}
                            userSet={userSet.current}
                            onToggle={handleToggle}
                            toggle={toggle}
                            excludeIds={memberIds}
                        />
                    </div>
                    <div className="flex flex-row justify-end gap-2 pt-2 border-t border-t-stone-200">
                        <Button onClick={() => { closePickGroup(); userSet.current.clear(); setFilteredName("") }}>انصراف</Button>
                        <Button variant="contained" color="success" onClick={handleAddUsers} sx={{ borderRadius: "0.5rem" }}>
                            {loading ? <CircularProgress size={20} color="inherit" /> : "افزودن"}
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Snackbar
                open={error.length !== 0}
                autoHideDuration={5000}
                onClose={handleClose}
                message={error}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                action={<GIcon className="cursor-pointer" onClick={handleClose} name="close" />}
            />
        </>
    )
}